import { useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Rut from "rutjs";
import * as XLSX from 'xlsx';
import api from '../../api/apiweb-massiveBoletaUploader.js';

const EmisorMasivo = () => {
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [errorMessageModal, setErrorMessageModal] = useState('');
  const [processedFolios, setProcessedFolios] = useState([]);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);


  const requiredColumns = [
    'rut',
    'razon social',
    'fecha emision',
    'valor neto',
    'iva',
    'valor total',
    'descripcion',
    'tipodepago',
  ];

  const normalizeKey = (key) =>
    key
      .trim()
      .toLowerCase()
      .normalize('NFD') // elimina acentos
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[\s_]+/g, ' ');

  const getColumnValue = (row, aliases) => {
    for (const alias of aliases) {
      if (row[alias] !== undefined && row[alias] !== '') {
        return row[alias];
      }
    }
    return '';
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const rawData = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });

      // Normalizar claves de cada fila
      const normalizedData = rawData.map(row => {
        const normalizedRow = {};
        Object.keys(row).forEach(key => {
          normalizedRow[normalizeKey(key)] = row[key];
        });
        return normalizedRow;
      });

      validateAndSetData(normalizedData);
    };
    reader.readAsArrayBuffer(file);
  };

  const capitalizeHeader = (header) => {

    if (header.toLowerCase() === 'tipodepago') {
      return 'Tipo de Pago';
    }

    return header
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const processRut = (rut) => {
    try {
      const parsed = new Rut(rut);
      // console.log('RUT procesado:', parsed.getNiceRut());
      // console.log('es válido:', parsed.validate());
      if (!parsed.validate()) return { valid: false };

      return {
        valid: true,
        rutWithoutDv: parsed.rut,       // solo número
        rutFormatted: parsed.getNiceRut(),    // con puntos y guion
      };
    } catch {
      return { valid: false };
    }
  };

  const formatAndValidateDate = (value) => {
    // console.log('Procesando fecha:', value);
    if (!value) {
      return { formatted: '', validFormat: false, isNotFuture: false };
    }

    let formatted = '';

    // Si es un objeto Date
    if (value instanceof Date) {
      formatted = value.toISOString().split('T')[0];
    }

    // Si es un número (formato serial de Excel)
    else if (typeof value === 'number') {
      const date = XLSX.SSF.parse_date_code(value);
      if (!date) return { formatted: '', validFormat: false, isNotFuture: false };
      formatted = `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
    }

    // Si es un string: limpiar y validar
    else {
      const cleaned = String(value)
        .trim()
        .replace(/[–—]/g, '-') // guiones raros
        .replace(/\s+/g, '');  // espacios internos

      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(cleaned)) {
        return { formatted: cleaned, validFormat: false, isNotFuture: false };
      }

      formatted = cleaned;
    }

    // Fecha de hoy en formato yyyy-mm-dd (sin zonas horarias)
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const todayStr = `${yyyy}-${mm}-${dd}`;

    const isNotFuture = formatted <= todayStr;

    // console.log('Fecha procesada:', formatted, 'formato válido:', true, 'es menor o igual a hoy:', isNotFuture);

    return {
      formatted,
      validFormat: true,
      isNotFuture,
    };
  };

  const validateAndSetData = (data) => {
    const headers = Object.keys(data[0] || {});
    const missingCols = requiredColumns.filter(col => !headers.includes(col));
    if (missingCols.length > 0) {
      setErrors([`Faltan las siguientes columnas: ${missingCols.join(', ')}`]);
      setRows([]);
      return;
    }

    const validatedRows = data.map((row) => {
      const rowErrors = [];

      // --- RUT ---
      const { valid, rutWithoutDv, rutFormatted } = processRut(row['rut']);
      if (!valid) {
        rowErrors.push('RUT inválido');
      } else {
        row['rut_display'] = rutFormatted;
        row['rut'] = rutWithoutDv;
      }

      // --- Razón social ---
      let razon = row['razon social']?.trim();

      if (!razon) {
        rowErrors.push('Razón social vacía');
      } else {
        // Normalizar: quitar tildes y pasar ñ/Ñ a N
        razon = razon.normalize("NFD").replace(/[\u0300-\u036f]/g, ""); // quita acentos
        razon = razon.replace(/ñ/gi, "N");

        // Reemplazar caracteres especiales no permitidos
        razon = razon.replace(/[^A-Za-z0-9\s.,-]/g, "");

        // Guardar la versión normalizada en el row
        row['razon social'] = razon;

        // Validar longitud
        if (razon.length > 60) {
          rowErrors.push('Razón social excede el máximo de 60 caracteres');
        }
      }

      // --- Números ---
      const valorNeto = parseFloat(row['valor neto']);
      const iva = parseFloat(row['iva']);
      const valorTotal = parseFloat(row['valor total']);
      if (isNaN(valorNeto) || valorNeto < 0) rowErrors.push('Valor neto inválido');
      if (isNaN(iva) || iva < 0) rowErrors.push('IVA inválido');
      if (isNaN(valorTotal) || valorTotal !== valorNeto + iva) rowErrors.push('Valor total no cuadra');

      // --- Fecha emision ---
      const rawFecha = getColumnValue(row, ['fecha emision', 'fecha_emision']);
      const { formatted: fechaEmision, validFormat, isNotFuture } = formatAndValidateDate(rawFecha);
      row['fecha emision'] = fechaEmision;
      if (!fechaEmision) {
        rowErrors.push('Fecha de emisión faltante');
      } else {
        if (!validFormat) rowErrors.push('Fecha de emisión inválida (debe ser yyyy-mm-dd)');
        else if (!isNotFuture) rowErrors.push('La fecha de emisión no puede ser mayor a hoy');
      }

      // --- tipodepago ---
      const rawPago = getColumnValue(row, ['tipodepago', 'tipo de pago', 'tipo_pago']);
      const normal = String(rawPago || '').trim().toLowerCase();

      // Sinónimos -> normalización
      const mapaPago = {
        'efectivo': 'efectivo',
        'cash': 'efectivo',
        'tarjeta': 'tarjeta',
        'credito': 'tarjeta',
        'crédito': 'tarjeta',
        'debito': 'tarjeta',
        'débito': 'tarjeta',
        'tdc': 'tarjeta',
        'tdd': 'tarjeta',
      };

      const tipodepago = mapaPago[normal];
      if (!tipodepago) {
        rowErrors.push('Tipo de pago inválido (permitidos: efectivo, tarjeta)');
      } else {
        row['tipodepago'] = tipodepago;
      }

      return { ...row, error: rowErrors.join('; ') };
    });

    setErrors([]);
    setRows(validatedRows);
  };

  const convertKeysToSnakeCase = (row) => ({
    rut: row.rut,
    razon_social: row['razon social'],
    fecha_emision: row['fecha emision'],
    valor_neto: row['valor neto'],
    iva: row.iva,
    valor_total: row['valor total'],
    descripcion: row.descripcion,
    tipodepago: row['tipodepago'],
  });

  const handleSend = () => {
    const hasErrores = rows.some(r => r.error);

    if (hasErrores) {
      setErrorMessageModal('Algunas filas contienen errores. Por favor revisa los datos y corrige los problemas antes de enviar.');
      setShowErrorModal(true);
      return;
    }

    const validRows = rows;
    const cleanRows = validRows.map(convertKeysToSnakeCase);
    const jsonString = JSON.stringify(cleanRows);

    // console.log("Enviando boletas:", jsonString);
    setLoading(true);
    api.uploadBoletas(jsonString)
      .then(response => {
        const payload = response.data?.body
          ? JSON.parse(response.data.body)
          : response.data;

        const folios = payload?.folios || payload?.assignedFolios || [];
        setProcessedFolios(folios);

        if (folios.length > 0) {
          setShowSuccessModal(true);

          // 🔹 Limpiar input y estados
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          setRows([]);
          setErrors([]);
        } else {
          console.warn('No se recibieron folios en la respuesta:', payload);
        }
      })
      .catch(error => {
        setErrorMessageModal('Ocurrió un error al enviar las boletas. Por favor intenta nuevamente.');
        setShowErrorModal(true);
        console.error('Error al enviar las boletas:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDownloadZip = async () => {
    try {
      setDownloadingZip(true);
      const res = await api.downloadZip({
        folios: JSON.stringify(processedFolios)
      });
      // console.log('Respuesta de descarga ZIP:', res);
      const url = res.data;

      //open url in new tab
      // console.log('URL del ZIP:', url);
      if (!url) {
        console.error('No se recibió una URL válida para el ZIP');
        return;
      }
      // console.log('Abriendo ZIP en nueva pestaña:', url);

      window.open(url, '_blank');
    } catch (err) {
      console.error('Error al descargar el ZIP:', err);
    } finally {
      setDownloadingZip(false);
    }
  };

  const handleDownloadTemplate = () => {

    const headers = [
      'rut',
      'razon social',
      'fecha emision',
      'valor neto',
      'iva',
      'valor total',
      'descripcion',
      'tipodepago',
    ];


    const exampleRows = [
      ['12345678-5', 'Juan Pérez', '2025-07-21', 100, 19, 119, 'Estacionamiento mayo 2025', 'efectivo'],
      ['11111111-1', 'María López', '2025-07-21', 200, 38, 238, 'Servicio mensual', 'tarjeta'],
    ];


    const aoa = [headers, ...exampleRows];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);


    const colWidths = [14, 22, 14, 12, 10, 12, 30, 14].map(w => ({ wch: w }));
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, 'plantilla_boletas.xlsx');
  };

  return (
    <div className="container mt-5">
      <div className="card shadow">
        <div className="card-body">


          <h3 className="card-title mb-4 text-center">📄 Subir archivo Excel</h3>



          <div className="mb-3">
            <div className="d-flex align-items-center justify-content-between">
              <label htmlFor="formFile" className="form-label fw-bold mb-0">
                Selecciona el archivo:
              </label>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleDownloadTemplate}
              >
                Descargar plantilla de ejemplo
              </button>
            </div>

            <input
              type="file"
              id="formFile"
              className="form-control"
              accept=".xlsx, .xls"
              onChange={handleFileUpload}
              ref={fileInputRef}
            />
          </div>

          {errors.length > 0 && (
            <div className="alert alert-danger mt-3">
              <h6 className="fw-bold">Errores detectados:</h6>
              {errors.map((e, idx) => <div key={idx}>• {e}</div>)}
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="table-responsive mt-4">
                <table className="table table-bordered table-striped align-middle">
                  <thead className="table-dark">
                    <tr>
                      {requiredColumns.map(col => (
                        <th key={col}>{capitalizeHeader(col)}</th>
                      ))}
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={i} className={row.error ? 'table-danger' : 'table-success'}>
                        {requiredColumns.map(col => (
                          <td key={col}>
                            {col === 'rut' && row['rut_display']
                              ? row['rut_display']
                              : row[col]}
                          </td>
                        ))}
                        <td>
                          {row.error
                            ? `❌ ${row.error}`
                            : '✅ Listo para emitir'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>



              <div className="d-flex justify-content-end">

                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => {
                    if (fileInputRef.current) fileInputRef.current.value = "";
                    setRows([]);
                    setErrors([]);
                    setProcessedFolios([]);
                  }}
                >
                  Limpiar
                </button>

                <button
                  className="btn btn-primary ms-2"
                  disabled={rows.some(r => r.error)}
                  onClick={handleSend}
                >
                  Emitir boletas
                </button>
              </div>

              <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
                <Modal.Header closeButton>
                  <Modal.Title>❌ Error al enviar</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {/* Algunas filas contienen errores. Por favor revisa los datos y corrige los problemas antes de enviar. */}
                  <p>{errorMessageModal}</p>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowErrorModal(false)}>
                    Cerrar
                  </Button>
                </Modal.Footer>
              </Modal>
            </>
          )}
        </div>
      </div>

      <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>✅ Boletas emitidas</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Se procesaron correctamente los siguientes folios:</p>
          <div className="border rounded p-2" style={{ maxHeight: 200, overflowY: 'auto' }}>
            {processedFolios.length > 0 ? (
              <ul className="mb-0">
                {processedFolios.map((f, idx) => <li key={idx}>Folio {f}</li>)}
              </ul>
            ) : (
              <em>No se recibieron folios.</em>
            )}
          </div>

          <p className="mt-3 mb-0">
            Puedes descargar un ZIP con los PDFs y un Excel con el detalle.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={handleDownloadZip}
            disabled={downloadingZip || processedFolios.length === 0}
          >
            {downloadingZip ? 'Preparando ZIP…' : 'Descargar ZIP'}
          </Button>
          <Button variant="secondary" onClick={() => setShowSuccessModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Modal de Cargando */}
      <Modal show={loading} centered backdrop="static">
        <Modal.Body className="text-center">
          <div className="my-3">
            <span className="spinner-border text-primary" role="status" />
          </div>
          <p>Cargando boletas, por favor espere</p>
        </Modal.Body>
      </Modal>
    </div>






  );

};

export default EmisorMasivo;
